import { Fragment } from 'react'
import { Col, Row } from 'antd'
import Card from 'components/card/Card'
import ButtonLink from 'components/button/Button'
import {
  AdminJobBoardsVacanciesGetJobVacancyDetailResponse,
  JobBoardsVacanciesStatusEnum,
} from 'generated/api'
import dayjs from 'dayjs'
import { rupiahFormat } from 'helpers/formatter'
import { thousandSeparator } from 'helpers/numbers'
import { mapJobVacancyCategory, mapJobVacancyStatus } from 'helpers/wording'
import Errors from 'components/errors/Errors'
import Badge from 'components/badge/Badge'
import jobVacancyStatusBadgeVariant from '../hooks/jobVacancyStatusBadgeVariant'

interface IBasicInformation {
  label: string
  value: string | number
}

interface ISoilInformation {
  label: string
  type: 'text' | 'image'
  value: string | string[]
}

interface IProps {
  fetchStatusJobVacancyDetail: 'loading' | 'idle' | 'error'
  jobVacancy: AdminJobBoardsVacanciesGetJobVacancyDetailResponse
  onOpenModalUpdateJobVacancyStatus: () => void
  onEditJobVacancy: (jobId: string) => void
  fetchJobVacancy: () => void
}

export default function JobVacancyDetailCard(props: IProps) {
  const {
    fetchStatusJobVacancyDetail,
    jobVacancy,
    onEditJobVacancy,
    fetchJobVacancy,
    onOpenModalUpdateJobVacancyStatus,
  } = props

  const loading = fetchStatusJobVacancyDetail === 'loading'
  const failed = fetchStatusJobVacancyDetail === 'error'

  const basicInformation: IBasicInformation[] = [
    {
      label: 'Tanggal Dibuat',
      value: jobVacancy.createdAtUtc0
        ? dayjs(jobVacancy.createdAtUtc0).format('DD MMMM YYYY')
        : '-',
    },
    {
      label: 'Kategori',
      value: jobVacancy.category.name ? mapJobVacancyCategory(jobVacancy.category) : '',
    },
    {
      label: 'Nama Pekerjaan',
      value: jobVacancy.jobName ? jobVacancy.jobName : '-',
    },
    {
      label: 'Jumlah Pelamar',
      value: jobVacancy.totalApplicant ? jobVacancy.totalApplicant : '-',
    },
    {
      label: 'Penawaran Harga',
      value: jobVacancy.wage
        ? `${rupiahFormat(jobVacancy.wage.minimum)} - ${rupiahFormat(jobVacancy.wage.maximum)} / ${jobVacancy.wage.unit}`
        : '-',
    },
  ]

  const facilitiesInformation: string[] = jobVacancy.facilities ? jobVacancy.facilities : []

  const soilInformation: ISoilInformation[] = [
    {
      label: 'Jenis Tanah',
      type: 'text',
      value: jobVacancy.terrain.soilType ? jobVacancy.terrain.soilType : '-',
    },
    {
      label: 'Kondisi Lahan',
      type: 'text',
      value: jobVacancy.terrain.condition ? jobVacancy.terrain.condition : '-',
    },
    {
      label: 'Tinggi Pohon',
      type: 'text',
      value: jobVacancy.terrain.minimumTreeHigh
        ? `<${jobVacancy.terrain.minimumTreeHigh} meter`
        : '-',
    },
    {
      label: 'Jarak Langsir',
      type: 'text',
      value:
        jobVacancy.ffb.minimumEstimation && jobVacancy.ffb.maximumEstimation
          ? `${thousandSeparator(jobVacancy.ffb.minimumEstimation)} - ${thousandSeparator(jobVacancy.ffb.maximumEstimation)} meter`
          : '-',
    },
    {
      label: 'Melangsir Dengan',
      type: 'text',
      value: jobVacancy.ffb.ffbCollectionMethod ? jobVacancy.ffb.ffbCollectionMethod : '-',
    },
    {
      label: 'Informasi Tambahan',
      type: 'text',
      value: jobVacancy.ffb.additionalParameter ? jobVacancy.ffb.additionalParameter : '-',
    },
    {
      label: 'Foto Lahan',
      type: 'image',
      value: jobVacancy.images ? jobVacancy.images : [],
    },
  ]

  const jobDescriptionHTML = jobVacancy.jobDescription ? jobVacancy.jobDescription : '-'

  return (
    <Card className="rounded-lg p-0" loading={loading}>
      {!failed && (
        <>
          <Card.Header className="p-4">
            <Row gutter={[8, 8]} className="w-full">
              <Col span={24} lg={12}>
                {jobVacancy.status && (
                  <div className="w-fit">
                    <Badge
                      title={mapJobVacancyStatus(jobVacancy.status)}
                      variant={jobVacancyStatusBadgeVariant(jobVacancy.status)}
                    />
                  </div>
                )}
              </Col>
              <Col span={24} lg={{ span: 2, offset: 6 }}>
                <ButtonLink
                  data-testid="button_edit_jobVacancy"
                  variant="outline-primary"
                  block={true}
                  label="UBAH"
                  className="text-sm w-full"
                  onClick={() => onEditJobVacancy(jobVacancy.id)}
                />
              </Col>
              <Col span={24} lg={4}>
                <ButtonLink
                  disabled={jobVacancy.status === JobBoardsVacanciesStatusEnum.Inactive}
                  data-testid="button_updateStatus_jobVacancy"
                  variant="solid-primary"
                  block={true}
                  label="Tandai Selesai"
                  className="text-sm w-full"
                  onClick={onOpenModalUpdateJobVacancyStatus}
                />
              </Col>
            </Row>
          </Card.Header>
          <div className="border-solid border-t border-0 border-[#E5E5E7]" />
          <Card.Body className="m-0 p-4">
            <Row gutter={[24, 24]}>
              <Col span={24} lg={8}>
                <span className="text-sm text-[#212121] font-bold">Informasi Dasar</span>
                <div className="mb-3" />
                <Row gutter={[8, 8]}>
                  {basicInformation.map(info => (
                    <Fragment key={info.label}>
                      <Col span={24} lg={12}>
                        <span className="text-xs text-[#757575] font-medium">{info.label}</span>
                      </Col>
                      <Col span={24} lg={12}>
                        <span className="text-xs text-[#212121] font-medium">{info.value}</span>
                      </Col>
                    </Fragment>
                  ))}
                </Row>
              </Col>
              <Col span={24} lg={8}>
                <span className="text-sm text-[#212121] font-bold">Fasilitas Kerja</span>
                <div className="mb-3" />
                <div className="flex flex-row flex-wrap gap-3">
                  {facilitiesInformation.map(info => (
                    <div
                      key={info}
                      className="rounded-lg bg-[#fff] border-solid border border-[#E5E5E7] px-2 py-1.5"
                    >
                      <span className="text-xs text-[#212121] font-normal">{info}</span>
                    </div>
                  ))}
                </div>
              </Col>
              <Col span={24} lg={8}>
                <span className="text-sm text-[#212121] font-bold">Informasi Lahan</span>
                <div className="mb-3" />
                <Row gutter={[8, 8]}>
                  {soilInformation.map(info => (
                    <Fragment key={info.label}>
                      <Col span={24} lg={12}>
                        <span className="text-xs text-[#757575] font-medium">{info.label}</span>
                      </Col>
                      {info.type === 'text' && typeof info.value === 'string' && (
                        <Col span={24} lg={12}>
                          <span className="text-xs text-[#212121] font-medium">{info.value}</span>
                        </Col>
                      )}
                      {info.type === 'image' && Array.isArray(info.value) && (
                        <Col span={24} lg={24}>
                          <div className="flex flex-row flex-wrap gap-2">
                            {info.value.map((image, index) => (
                              <img
                                key={index}
                                src={image}
                                alt="soil"
                                className="w-1/4 object-cover p-2 rounded-lg border-solid border border-[#E5E5E7]"
                              />
                            ))}
                          </div>
                        </Col>
                      )}
                    </Fragment>
                  ))}
                </Row>
              </Col>
            </Row>
          </Card.Body>
          <div className="border-solid border-t border-0 border-[#E5E5E7] mx-4" />
          <Card.Footer>
            <div className="p-4">
              <span className="text-sm text-[#212121] font-bold">Deskripsi Pekerjaan</span>
              <div
                className="text-xs text-[#212121]"
                dangerouslySetInnerHTML={{ __html: jobDescriptionHTML }}
              />
            </div>
          </Card.Footer>
        </>
      )}
      {failed && (
        <div className="pb-2">
          <Errors
            data-testid="error_fetch_job_applicant"
            inverse={false}
            header="Mohon maaf, permintaan Anda tidak dapat diproses."
            paragraph="terjadi kesalahan internal pada server kami. Silakan coba beberapa saat lagi."
            button={true}
            buttonLabel="Coba lagi"
            buttonAction={fetchJobVacancy}
          />
        </div>
      )}
    </Card>
  )
}
